import axios from 'axios';
import { globalConfig } from '../configuration/config';

const PAGE_SIZE = 20;
const METADATA_CONCURRENCY = 4;

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new DOMException('Variant tree loading was cancelled.', 'AbortError');
  }
}

function readGuid(value) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }
  return value;
}

export function readVariantProvenance(receipt, calculationGuid) {
  const value = receipt?.calculationProvenance;
  const result = {
    source: undefined,
    originCalculationGuid: null,
    knownAppliedOptimizations: [],
    warning: false,
  };
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return { ...result, warning: true };
  }

  if (value.source?.kind === 'glb') {
    result.source = { kind: 'glb' };
  } else if (
    value.source?.kind === 's123' &&
    (value.source.calculationGuid === null || readGuid(value.source.calculationGuid) !== null)
  ) {
    result.source = { kind: 's123', calculationGuid: value.source.calculationGuid };
  } else {
    result.warning = true;
  }

  result.originCalculationGuid = readGuid(value.originCalculationGuid) ??
    (result.source?.kind === 'glb' ? calculationGuid : null);
  if (value.originCalculationGuid != null && readGuid(value.originCalculationGuid) === null) {
    result.warning = true;
  }

  if (Array.isArray(value.knownAppliedOptimizations)) {
    const validCodes = value.knownAppliedOptimizations.filter(
      (code) => typeof code === 'string' && code.trim().length > 0,
    );
    result.knownAppliedOptimizations = [...new Set(validCodes)].sort();
    result.warning ||= validCodes.length !== value.knownAppliedOptimizations.length;
  } else {
    result.warning = true;
  }
  return result;
}

export function findVariantReceipts(responseData) {
  const responseBody = responseData?.data ?? responseData;
  const calculationStat = responseBody?.tVal ?? responseBody?.TVal ?? responseBody;
  const calculationFiles = calculationStat?.calculationFiles ?? calculationStat?.CalculationFiles;
  if (!Array.isArray(calculationFiles)) {
    throw new Error('Calculation file list is unavailable.');
  }

  const receipts = [];
  for (const relation of calculationFiles) {
    const file = relation?.file ?? relation?.File ?? relation;
    if (
      (relation?.isEnabled ?? relation?.IsEnabled) === false ||
      (file?.isEnabled ?? file?.IsEnabled) === false
    ) {
      continue;
    }
    const name = file?.name ?? file?.Name ?? relation?.name ?? relation?.Name;
    if (typeof name !== 'string') {
      continue;
    }
    const fileName = name.replaceAll('\\', '/').split('/').pop().toLowerCase();
    if (
      fileName === 'transformation-receipt.json' ||
      (!fileName.startsWith('receipt-glb-') && /^receipt-.+\.json$/u.test(fileName))
    ) {
      receipts.push({ name, path: file?.path ?? file?.Path ?? relation?.path ?? relation?.Path });
    }
  }
  return receipts;
}

async function loadVariantProvenance({ calculationGuid, apiBaseUrl, signal }) {
  const unknown = {
    source: undefined,
    originCalculationGuid: null,
    knownAppliedOptimizations: [],
    warning: false,
  };
  try {
    const response = await axios.get(`${apiBaseUrl}/Calculation/GetCalculationStat`, {
      params: { guid: calculationGuid },
      withCredentials: true,
      signal,
    });
    throwIfAborted(signal);
    const receipts = findVariantReceipts(response.data);
    if (receipts.length === 0) {
      return unknown;
    }
    if (receipts.length !== 1 || readGuid(receipts[0].path) === null) {
      return { ...unknown, warning: true };
    }
    const receiptResponse = await axios.get(`${apiBaseUrl}/Data/DownloadFile`, {
      params: { pathToFile: receipts[0].path },
      withCredentials: true,
      signal,
    });
    throwIfAborted(signal);
    let receipt = receiptResponse.data;
    if (typeof Blob !== 'undefined' && receipt instanceof Blob) {
      receipt = await receipt.text();
      throwIfAborted(signal);
    }
    return readVariantProvenance(
      typeof receipt === 'string' ? JSON.parse(receipt) : receipt,
      calculationGuid,
    );
  } catch (error) {
    throwIfAborted(signal);
    return { ...unknown, warning: true };
  }
}

export async function loadProjectVariants({ projectGuid, signal, onUpdate, untilCalculationGuid }) {
  const apiBaseUrl = globalConfig.config.common.api.replace(/\/+$/u, '');
  const variants = [];
  const calculationGuids = new Set();
  let listError = false;

  // Only the limited initial response includes a base project's root calculation.
  // The complete response avoids the endpoint's timestamp-based cursor, which can skip shortcuts.
  for (const initialRequest of [true, false]) {
    throwIfAborted(signal);
    let page;
    try {
      const response = await axios.get(`${apiBaseUrl}/ProjectVariants/GetVariants`, {
        params: {
          guidProject: projectGuid,
          flag: false,
          isUp: false,
          ...(initialRequest ? { limit: PAGE_SIZE } : {}),
        },
        withCredentials: true,
        signal,
      });
      throwIfAborted(signal);
      page = response.data;
      if (!Array.isArray(page)) {
        throw new Error('Project variant list is unavailable.');
      }
    } catch (error) {
      throwIfAborted(signal);
      listError = true;
      continue;
    }

    for (const variant of page) {
      const calculationGuid = readGuid(variant?.calculationGuid);
      if (calculationGuid === null) {
        listError = true;
      } else if (!calculationGuids.has(calculationGuid)) {
        calculationGuids.add(calculationGuid);
        variants.push({ ...variant, projectGuid });
      }
    }
    onUpdate?.({ variants: [...variants], listError });
    if (page.length < PAGE_SIZE || calculationGuids.has(untilCalculationGuid)) {
      break;
    }
  }
  return { variants, listError };
}

export async function loadVariantTree({ projectGuid, signal, onUpdate }) {
  const apiBaseUrl = globalConfig.config.common.api.replace(/\/+$/u, '');
  const { variants, listError } = await loadProjectVariants({
    projectGuid,
    signal,
    onUpdate: (page) => onUpdate({
      entries: page.variants.map((variant) => ({ variant, provenance: undefined })),
      listError: page.listError,
      loading: true,
    }),
  });
  const entries = variants.map((variant) => ({ variant, provenance: undefined }));

  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(METADATA_CONCURRENCY, entries.length) }, async () => {
    while (nextIndex < entries.length) {
      throwIfAborted(signal);
      const index = nextIndex++;
      const provenance = await loadVariantProvenance({
        calculationGuid: entries[index].variant.calculationGuid,
        apiBaseUrl,
        signal,
      });
      throwIfAborted(signal);
      entries[index] = { ...entries[index], provenance };
      onUpdate({ entries: [...entries], listError, loading: true });
    }
  });
  await Promise.all(workers);
  throwIfAborted(signal);
  const result = { entries: [...entries], listError, loading: false };
  onUpdate(result);
  return result;
}

export function buildVariantForest(entries) {
  const nodes = new Map(entries.map((entry) => [
    entry.variant.calculationGuid,
    { entry, children: [], parentGuid: null, relationIssue: false },
  ]));

  for (const [calculationGuid, node] of nodes) {
    const source = node.entry.provenance?.source;
    const parentGuid = source?.kind === 's123' ? source.calculationGuid : null;
    if (parentGuid && nodes.has(parentGuid) && parentGuid !== calculationGuid) {
      node.parentGuid = parentGuid;
    } else if (parentGuid) {
      node.relationIssue = true;
    }
  }

  const visited = new Set();
  for (const calculationGuid of nodes.keys()) {
    const path = [];
    const pathIndices = new Map();
    let currentGuid = calculationGuid;
    while (currentGuid !== null && !visited.has(currentGuid)) {
      if (pathIndices.has(currentGuid)) {
        for (const cycleGuid of path.slice(pathIndices.get(currentGuid))) {
          nodes.get(cycleGuid).parentGuid = null;
          nodes.get(cycleGuid).relationIssue = true;
        }
        break;
      }
      pathIndices.set(currentGuid, path.length);
      path.push(currentGuid);
      currentGuid = nodes.get(currentGuid).parentGuid;
    }
    for (const visitedGuid of path) {
      visited.add(visitedGuid);
    }
  }

  const knownRoots = [];
  const unknownRoots = [];
  for (const node of nodes.values()) {
    if (node.parentGuid !== null) {
      nodes.get(node.parentGuid).children.push(node);
    } else if (node.entry.provenance?.source?.kind === 'glb') {
      knownRoots.push(node);
    } else {
      unknownRoots.push(node);
    }
  }

  // Iterative traversal also permits long, valid optimization histories.
  const groups = {};
  for (const [group, roots] of [['known', knownRoots], ['unknown', unknownRoots]]) {
    const rows = [];
    const pending = roots.map((node) => ({ node, depth: 0 })).reverse();
    while (pending.length > 0) {
      const { node, depth } = pending.pop();
      rows.push({ entry: node.entry, depth, relationIssue: node.relationIssue });
      for (const child of [...node.children].reverse()) {
        pending.push({ node: child, depth: depth + 1 });
      }
    }
    groups[group] = rows;
  }
  return groups;
}
